import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import useRequest from "../../api/useRequest";
import CardContainer from "../../components/cards/CardContainer";
import SubscriptionTerms from "../../components/SubscriptionTerms";
import { tokens } from "../../theme/theme";

const statusColors = {
  active: "success",
  trialing: "info",
  paid: "success",
  open: "warning",
  past_due: "warning",
  partially_refunded: "warning",
  failed: "error",
  unpaid: "error",
  refunded: "default",
  canceled: "default",
  void: "default",
};

const statusLabels = {
  active: "Active",
  trialing: "Trial",
  paid: "Paid",
  open: "Open",
  past_due: "Past due",
  partially_refunded: "Partially refunded",
  failed: "Failed",
  unpaid: "Unpaid",
  refunded: "Refunded",
  canceled: "Canceled",
  void: "Void",
  paused: "Paused",
  incomplete: "Incomplete",
};

const purchaseTypeLabels = {
  credit_pack: "Credit pack",
  subscription: "Subscription",
};

const localeFor = (language) => (language === "de" ? "de-DE" : "en-US");

export const formatMoney = (amount, currency, language) => {
  if (amount === null || amount === undefined || !currency) return "–";
  return new Intl.NumberFormat(localeFor(language), {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);
};

export const formatDate = (value, language) => {
  if (!value) return "–";
  return new Intl.DateTimeFormat(localeFor(language), {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

const StatusChip = ({ status, t }) => (
  <Chip
    size="small"
    color={statusColors[status] || "default"}
    label={t(statusLabels[status] || status || "Unknown")}
  />
);

const CurrentSubscription = ({ subscription, language, t }) => (
  <CardContainer className="items-start p-4" sx={{ minHeight: 170 }}>
    <Typography variant="h3">{t("Current subscription")}</Typography>
    {!subscription ? (
      <Typography variant="h5" sx={{ mt: 2 }}>
        {t("No active subscription")}
      </Typography>
    ) : (
      <Box className="flex flex-col gap-2 w-full" sx={{ mt: 2 }}>
        <Box className="flex flex-row gap-3 items-center flex-wrap">
          <Typography variant="h4">{subscription.name}</Typography>
          <StatusChip status={subscription.status} t={t} />
        </Box>
        <Typography>
          {formatMoney(subscription.amount, subscription.currency, language)}
          {subscription.interval ? ` / ${t(subscription.interval)}` : ""}
        </Typography>
        {subscription.credits !== null && subscription.credits !== undefined && (
          <Typography>
            {Number(subscription.credits).toLocaleString(localeFor(language))} {t("Credits per period")}
          </Typography>
        )}
        <SubscriptionTerms subscription={subscription} language={language} />
        {subscription.current_period_end && !subscription.cancel_at_period_end && (
          <Typography>
            {t("Next billing date")}: {formatDate(subscription.current_period_end, language)}
          </Typography>
        )}
        {(subscription.cancellation_scheduled || subscription.cancel_at || subscription.cancel_at_period_end) && (
          <Typography>
            {t("Ends on")}: {formatDate(subscription.cancel_at || subscription.current_period_end, language)}
          </Typography>
        )}
      </Box>
    )}
  </CardContainer>
);

const PurchaseHistory = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const language = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const path = useMemo(
    () => `billing-history?page=${page}&page_size=${pageSize}`,
    [page, pageSize]
  );
  const { apiData, isLoading, serverError, reloadApiData } = useRequest({ path });
  const purchases = apiData?.purchases || [];
  const pagination = apiData?.pagination || { total: 0 };

  return (
    <Box className="flex flex-col gap-6">
      <Box>
        <Typography variant="h2">{t("Purchase history")}</Typography>
        <Typography variant="h4" sx={{ color: colors.contrast[700], mt: 1 }}>
          {t("Subscription and purchases for your entire tenant.")}
        </Typography>
      </Box>

      {apiData?.stale && (
        <Alert severity="warning">
          {t("Stripe is temporarily unavailable. Cached billing data is shown.")}
        </Alert>
      )}

      {isLoading ? (
        <Box
          className="flex flex-col justify-center items-center gap-3 p-8"
          sx={{ minHeight: 320 }}
          aria-live="polite"
        >
          <CircularProgress aria-label={t("Loading purchase history...")} />
          <Typography variant="h5">
            {t("Loading purchase history...")}
          </Typography>
        </Box>
      ) : serverError && !apiData ? (
        <Alert
          severity="error"
          action={<Button onClick={reloadApiData}>{t("Try again")}</Button>}
        >
          {t("Purchase history could not be loaded.")}
        </Alert>
      ) : (
        <>
          <CurrentSubscription
            subscription={apiData?.current_subscription}
            language={language}
            t={t}
          />

          <Box className="flex flex-col gap-3">
            <Box className="flex flex-row gap-2 items-center">
              <ReceiptLongIcon />
              <Typography variant="h3">{t("Purchases and invoices")}</Typography>
            </Box>
            <TableContainer component={Paper}>
              <Table aria-label={t("Purchase history")}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("Date")}</TableCell>
                    <TableCell>{t("Type")}</TableCell>
                    <TableCell>{t("Product")}</TableCell>
                    <TableCell align="right">{t("Credits")}</TableCell>
                    <TableCell align="right">{t("Amount")}</TableCell>
                    <TableCell>{t("Status")}</TableCell>
                    <TableCell align="right">{t("Receipt")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchases.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        {t("No purchases or invoices yet.")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    purchases.map((purchase) => (
                      <TableRow key={purchase.id} hover>
                        <TableCell>{formatDate(purchase.occurred_at, language)}</TableCell>
                        <TableCell>{t(purchaseTypeLabels[purchase.type] || purchase.type)}</TableCell>
                        <TableCell>{purchase.name}</TableCell>
                        <TableCell align="right">
                          {purchase.credits === null || purchase.credits === undefined
                            ? "–"
                            : Number(purchase.credits).toLocaleString(localeFor(language))}
                        </TableCell>
                        <TableCell align="right">
                          {formatMoney(purchase.amount_total, purchase.currency, language)}
                        </TableCell>
                        <TableCell><StatusChip status={purchase.status} t={t} /></TableCell>
                        <TableCell align="right">
                          {purchase.document_url ? (
                            <Link
                              href={purchase.document_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              color="primary"
                              aria-label={`${t("Open receipt")}: ${purchase.name}`}
                            >
                              <OpenInNewIcon fontSize="small" color="primary" />
                            </Link>
                          ) : "–"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={pagination.total || 0}
                page={page - 1}
                onPageChange={(_event, nextPage) => setPage(nextPage + 1)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(1);
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
              />
            </TableContainer>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PurchaseHistory;
